import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import DateInput from "../components/DateInput";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, FilePdf, Sparkle, SealCheck, GitBranch, Trash, Archive } from "@phosphor-icons/react";
import { useBrandedPdf } from "../components/hse/useBrandedPdf";
import { HSE_STATUS_CLASS } from "../components/hse/HseDocumentsPanel";
import HseApproveDialog from "../components/hse/HseApproveDialog";
import HseSectionEditor from "../components/hse/HseSectionEditor";
import HseAckCard from "../components/hse/HseAckCard";
import HseQuizCard from "../components/hse/HseQuizCard";
import { fmtDate, fmtDateTime } from "../lib/dateUtil";

function Generating({ doc }) {
  return (
    <div className="bg-card border border-border rounded-lg p-10 text-center" data-testid="hse-doc-generating">
      <Sparkle size={28} className="mx-auto text-primary animate-pulse" />
      <p className="font-[Chivo] font-semibold mt-3">The AI is writing your {doc.type_label}…</p>
      <p className="text-xs text-muted-foreground mt-1">Usually 30–90 seconds. This page refreshes itself — you can leave and come back.</p>
    </div>
  );
}

export default function HseDocumentPage() {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const [showRegen, setShowRegen] = useState(false);
  const [brief, setBrief] = useState("");

  const load = useCallback(() => API.get(`/hse/documents/${docId}`).then(({ data }) => { setDoc(data); setDirty(false); }).catch(e => toast.error(formatApiError(e))), [docId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (doc?.ai?.status !== "pending") return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [doc?.ai?.status, load]);

  const set = (patch) => { setDoc(d => ({ ...d, ...patch })); setDirty(true); };
  const editable = doc?.status === "draft" && doc?.ai?.status !== "pending";

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put(`/hse/documents/${docId}`, { title: doc.title, summary: doc.summary, sections: doc.sections, key_points: doc.key_points, review_date: doc.review_date });
      setDoc(d => ({ ...d, ...data })); setDirty(false); toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const regenerate = async () => {
    try { await API.post(`/hse/documents/${docId}/regenerate`, { brief }); setShowRegen(false); setBrief(""); toast.success("Rewriting with your instructions…"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const revise = async () => {
    try { const { data } = await API.post(`/hse/documents/${docId}/revise`); toast.success(`v${data.version} draft created`); navigate(`/health-safety/docs/${data.id}`); } catch (e) { toast.error(formatApiError(e)); }
  };
  const archive = async () => {
    if (!window.confirm("Withdraw this approved document? It will no longer count as live.")) return;
    try { await API.post(`/hse/documents/${docId}/archive`); toast.success("Withdrawn"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete draft ${doc.reference} v${doc.version}?`)) return;
    try { await API.delete(`/hse/documents/${docId}`); toast.success("Deleted"); navigate("/health-safety?tab=documents"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const { openBranded, brandingDialog } = useBrandedPdf();
  const pdf = () => openBranded({ url: `/hse/documents/${docId}/pdf`, filename: `${doc.reference}_v${doc.version}.pdf`, title: `${doc.reference} PDF branding` });

  if (!doc) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="hse-document-page">
      <div className="flex flex-wrap items-start gap-3">
        <Link to="/health-safety?tab=documents" className="mt-1 text-muted-foreground hover:text-foreground" data-testid="hse-doc-back"><ArrowLeft size={18} /></Link>
        <div className="flex-1 min-w-[260px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-muted-foreground">{doc.reference} · v{doc.version}</span>
            <Badge className={`text-[10px] ${HSE_STATUS_CLASS[doc.status] || ""}`} data-testid="hse-doc-status">{doc.status === "archived" ? "withdrawn" : doc.status}</Badge>
            <span className="text-xs text-muted-foreground">{doc.type_label} · {doc.legal}</span>
          </div>
          {editable ? <Input value={doc.title} onChange={e => set({ title: e.target.value })} className="mt-1 h-9 text-lg font-[Chivo] font-semibold" data-testid="hse-doc-title" />
            : <h1 className="mt-1 text-xl sm:text-2xl font-[Chivo] font-bold tracking-tight" data-testid="hse-doc-title">{doc.title}</h1>}
        </div>
        <div className="flex flex-wrap gap-2">
          {editable && <Button size="sm" variant="outline" onClick={() => setShowRegen(true)} data-testid="hse-doc-regenerate-btn"><Sparkle size={14} className="mr-1" /> Rewrite with AI</Button>}
          {editable && <Button size="sm" variant="outline" onClick={save} disabled={saving || !dirty} data-testid="hse-doc-save-btn"><FloppyDisk size={14} className="mr-1" /> {saving ? "Saving…" : "Save"}</Button>}
          {editable && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => (dirty ? toast.message("Save your changes first") : setShowApprove(true))} data-testid="hse-doc-approve-btn"><SealCheck size={14} className="mr-1" /> Approve &amp; sign</Button>}
          {doc.status !== "draft" && <Button size="sm" variant="outline" onClick={revise} data-testid="hse-doc-revise-btn"><GitBranch size={14} className="mr-1" /> New version</Button>}
          <Button size="sm" variant="outline" onClick={pdf} data-testid="hse-doc-pdf-btn"><FilePdf size={14} className="mr-1" /> PDF</Button>
          {doc.status === "approved" && <Button size="sm" variant="ghost" onClick={archive} data-testid="hse-doc-archive-btn"><Archive size={14} className="mr-1" /> Withdraw</Button>}
          {doc.status === "draft" && <Button size="sm" variant="ghost" className="text-red-600" onClick={remove} data-testid="hse-doc-delete-btn"><Trash size={14} /></Button>}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
        <div className="space-y-4">
          {doc.ai?.status === "pending" && <Generating doc={doc} />}
          {doc.ai?.status === "failed" && (
            <div className="border border-red-200 bg-red-50 text-red-800 rounded-lg p-4 text-sm" data-testid="hse-doc-ai-failed">
              The AI draft failed: {doc.ai.error || "unknown error"}. <button type="button" className="underline" onClick={() => setShowRegen(true)}>Try again</button>.
            </div>
          )}
          {doc.ai?.status !== "pending" && (
            <HseSectionEditor doc={doc} editable={editable} onChange={set} />
          )}
        </div>
        <aside className="space-y-3">
          <div className="bg-card border border-border rounded-lg p-4 text-xs space-y-2" data-testid="hse-doc-control">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Document control</p>
            <Row k="Prepared by" v={`${doc.prepared_by_name || "—"} · ${fmtDate(doc.created_at)}`} />
            <Row k="Approved by" v={doc.approved_at ? `${doc.approved_by_name}${doc.approved_by_role ? ` (${doc.approved_by_role})` : ""} · ${fmtDateTime(doc.approved_at)}` : "Awaiting approval"} />
            <Row k="Signature" v={doc.signature_name || "—"} />
            <div className="flex items-center justify-between gap-2"><span className="text-muted-foreground">Next review</span>
              {editable ? <DateInput value={doc.review_date || ""} onChange={e => set({ review_date: e.target.value })} className="w-40 inline-block align-middle" data-testid="hse-doc-review-date" /> : <span className={doc.review_state === "overdue" ? "text-red-700 font-semibold" : doc.review_state === "due" ? "text-amber-700 font-semibold" : ""}>{doc.review_date ? fmtDate(doc.review_date) : "—"}</span>}
            </div>
            {doc.supersedes_reference && <Row k="Supersedes" v={doc.supersedes_reference} />}
            {doc.ai?.generated_at && <Row k="AI drafted" v={fmtDateTime(doc.ai.generated_at)} />}
          </div>
          {doc.status !== "draft" && <HseAckCard doc={doc} onChanged={load} />}
          {doc.status !== "draft" && doc.requires_ack && <HseQuizCard doc={doc} onChanged={load} />}
          {doc.versions?.length > 1 && (
            <div className="bg-card border border-border rounded-lg p-4 text-xs" data-testid="hse-doc-versions">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Versions</p>
              {doc.versions.map(v => (
                <Link key={v.id} to={`/health-safety/docs/${v.id}`} className={`flex items-center justify-between py-1 ${v.id === doc.id ? "font-semibold" : "hover:underline"}`} data-testid={`hse-doc-version-${v.version}`}>
                  <span>v{v.version} <Badge className={`ml-1 text-[9px] ${HSE_STATUS_CLASS[v.status] || ""}`}>{v.status}</Badge></span>
                  <span className="text-muted-foreground">{v.approved_at ? fmtDate(v.approved_at) : fmtDate(v.created_at)}</span>
                </Link>
              ))}
            </div>
          )}
          {!!doc.legal_refs?.length && (
            <div className="bg-card border border-border rounded-lg p-4 text-xs" data-testid="hse-doc-legal">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Legislation relied on</p>
              <ul className="list-disc pl-4 space-y-0.5">{doc.legal_refs.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </div>
          )}
          {!!doc.questions?.length && (
            <div className="bg-card border border-border rounded-lg p-4 text-xs" data-testid="hse-doc-answers">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Your answers</p>
              {doc.questions.filter(qd => doc.answers?.[qd.key] && String(doc.answers[qd.key]).length).map(qd => (
                <div key={qd.key} className="py-1 border-b border-border/60 last:border-0"><span className="text-muted-foreground">{qd.label}:</span> {Array.isArray(doc.answers[qd.key]) ? doc.answers[qd.key].join(", ") : String(doc.answers[qd.key])}</div>
              ))}
            </div>
          )}
        </aside>
      </div>

      <HseApproveDialog open={showApprove} onOpenChange={setShowApprove} doc={doc} onApproved={load} />
      <Dialog open={showRegen} onOpenChange={setShowRegen}>
        <DialogContent className="max-w-lg" data-testid="hse-regen-dialog">
          <DialogHeader><DialogTitle className="font-[Chivo]">Rewrite with AI</DialogTitle><p className="text-xs text-muted-foreground">Tell the AI what to change. The whole document is rewritten from your profile and answers plus these instructions.</p></DialogHeader>
          <Textarea value={brief} onChange={e => setBrief(e.target.value)} rows={4} placeholder="e.g. Make the check-in procedure stricter — 2-hourly calls — and add a section on working in void properties." data-testid="hse-regen-brief" />
          <DialogFooter><Button variant="outline" onClick={() => setShowRegen(false)}>Cancel</Button><Button onClick={regenerate} data-testid="hse-regen-confirm"><Sparkle size={14} className="mr-1" /> Rewrite</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      {brandingDialog}
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex items-start justify-between gap-2"><span className="text-muted-foreground shrink-0">{k}</span><span className="text-right">{v}</span></div>;
}

